import { useState } from 'react'
import { openFilePreview, openFilePreviewByPath } from '@/lib/utils/filePreview'
import './Thumbnail.css'

interface ThumbnailProps {
  /** File path or URL */
  filepath: string
  /** File attachment ID (for secure preview) */
  attachmentId?: string | number
  /** File type (e.g., 'ID_PROOF', 'PROOF_OF_INCOME') */
  fileType?: string
  /** File name */
  fileName?: string
  /** Reference ID (applicant ID or resident ID) */
  refId?: string | number
  /** Module name (e.g., 'APPLICATION', 'NOTES') */
  module?: string
  /** Optional array of file URLs for navigation arrows */
  filesList?: string[]
  /** Custom click handler (overrides default preview) */
  onClick?: () => void
  /** Custom CSS class */
  className?: string
  /** Custom style */
  style?: React.CSSProperties
  /** Error handler */
  onError?: () => void
}

/**
 * Thumbnail component for displaying images with preview functionality
 * Handles image loading errors and provides click-to-preview functionality
 */
export function Thumbnail({
  filepath,
  attachmentId,
  fileType,
  fileName,
  refId,
  module = 'APPLICATION',
  filesList,
  onClick,
  className = '',
  style,
  onError,
}: ThumbnailProps) {
  const [loadError, setLoadError] = useState(false)

  const handleError = () => {
    setLoadError(true)
    if (onError) {
      onError()
    }
  }

  const handleClick = () => {
    if (onClick) {
      onClick()
      return
    }

    // If we have attachment ID and other required params, use secure preview (async: ensures fresh token)
    if (attachmentId && fileType && refId) {
      void openFilePreview(attachmentId, fileType, fileName || filepath, refId, module, filesList)
    } else {
      openFilePreviewByPath(filepath, fileName, filesList)
    }
  }

  // Don't render if there's no filepath or if there's a load error
  if (!filepath || loadError) {
    return null
  }

  return (
    <div className={`thumbnail-container ${className}`} style={style}>
      <img
        className="thumbnail-image"
        src={filepath}
        alt={fileName || 'Thumbnail'}
        onClick={handleClick}
        onError={handleError}
      />
    </div>
  )
}

